import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { ArrowLeft, BookOpen, Target, Trophy, Calendar } from 'lucide-react';
import DailyLesson from './DailyLesson';
import MiniLeaderboard from './MiniLeaderboard';
import { progressAPI, passageAPI, leaderboardAPI } from '../services/api';

export default function Dashboard({ profile, session, onBack, onBackToProfiles }) {
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [userProgress, setUserProgress] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lessonPoints, setLessonPoints] = useState({}); // Store points per lesson

  useEffect(() => {
    window.scrollTo(0, 0);
    loadDashboardData();
  }, [profile.id, session.session_id]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      // Load session lessons and progress
      const sessionIdTrimmed = (session.session_id || '').trim();
      const [lessonsResponse, progressResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/study-sessions/${sessionIdTrimmed}/lessons`, {
          credentials: 'include'
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/progress/session/${profile.id}/${sessionIdTrimmed}`, {
          credentials: 'include'
        })
      ]);
      
      const lessonsData = await lessonsResponse.json();
      const progressData = await progressResponse.json();
      
      setLessons(lessonsData.lessons || []);
      setUserProgress(progressData);
      
      // Fetch lesson points for each lesson
      if (lessonsData.lessons && lessonsData.lessons.length > 0) {
        const pointsPromises = lessonsData.lessons.map(async (lesson) => {
          try {
            const pointsData = await leaderboardAPI.getProfileLessonPoints(lesson.lesson_id, profile.id);
            return { lessonId: lesson.lesson_id, points: pointsData.points || 0 };
          } catch (error) {
            console.error(`Error fetching points for lesson ${lesson.lesson_id}:`, error);
            return { lessonId: lesson.lesson_id, points: 0 };
          }
        });
        
        const pointsResults = await Promise.all(pointsPromises);
        const pointsMap = {};
        pointsResults.forEach(result => {
          pointsMap[result.lessonId] = result.points;
        });
        setLessonPoints(pointsMap);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const currentDay = userProgress?.current_day || 1;
  const completedDays = userProgress?.completed_days?.length || 0;
  const totalDays = lessons.length;

  const handleLessonComplete = async (lessonDay, earnedPoints) => {
    try {
      console.log('Completing lesson:', { lessonDay, earnedPoints, profileId: profile.id, sessionId: session.session_id });
      
      // Complete lesson via API using session-based endpoint
      const sessionIdTrimmed = (session.session_id || '').trim();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/progress/session/${profile.id}/${sessionIdTrimmed}/complete-lesson`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            day_number: lessonDay,
            earned_points: earnedPoints
          })
        }
      );
      
      // Read response body once
      const responseData = await response.json().catch(() => null);
      
      if (!response.ok) {
        console.error('Lesson completion failed:', responseData);
        throw new Error(responseData?.detail || `Server error: ${response.status}`);
      }
      
      const updatedProgress = responseData;
      console.log('Lesson completed successfully:', updatedProgress);
      
      // Update local state
      setUserProgress(updatedProgress);
      
      // Re-fetch lesson points for the completed lesson to update the green badge immediately
      try {
        const completedLesson = lessons.find(l => l.day_number === lessonDay);
        if (completedLesson) {
          const pointsData = await leaderboardAPI.getProfileLessonPoints(completedLesson.lesson_id, profile.id);
          setLessonPoints(prev => ({
            ...prev,
            [completedLesson.lesson_id]: pointsData.points || 0
          }));
          console.log('Updated lesson points:', completedLesson.lesson_id, pointsData.points);
        }
      } catch (pointsError) {
        console.error('Error updating lesson points:', pointsError);
        // Don't fail the whole operation if points fetch fails
      }
      
      // Re-fetch profile to get updated streak
      try {
        const updatedProfiles = await profileAPI.getAll();
        const updatedProfile = updatedProfiles.find(p => p.id === profile.id);
        if (updatedProfile) {
          // Update the profile object in place so streak displays immediately
          profile.streak = updatedProfile.streak;
          profile.totalPoints = updatedProfile.totalPoints;
          console.log('Updated profile streak:', updatedProfile.streak);
        }
      } catch (profileError) {
        console.error('Error updating profile:', profileError);
      }
      
      setSelectedLesson(null);
    } catch (error) {
      console.error('Error completing lesson:', error);
      alert(`Failed to complete lesson: ${error.message}. Please try again.`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center">
        <p className="text-xl text-blue-700">Loading...</p>
      </div>
    );
  }

  if (selectedLesson) {
    return (
      <DailyLesson
        lesson={selectedLesson}
        profile={profile}
        sessionId={session.session_id}
        session={session}
        onBack={() => {
          setSelectedLesson(null);
          // Reload dashboard data to refresh lesson points
          loadDashboardData();
        }}
        onComplete={handleLessonComplete}
        progress={userProgress?.day_progress?.[selectedLesson.day_number] || {}}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Header - Now Sticky */}
      <div className="bg-white border-b border-blue-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={onBack} className="text-blue-700 hover:text-blue-900">
                <ArrowLeft className="mr-2" /> Back to Sessions
              </Button>
              <div className="border-l border-gray-300 pl-4">
                <p className="text-sm text-gray-600">Current Study</p>
                <p className="font-semibold text-blue-900">{session.title}</p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <img 
                  src={profile.avatar} 
                  alt={profile.name}
                  className="w-16 h-16 mx-auto mb-1 rounded-full object-cover"
                  onError={(e) => {
                    e.target.src = '/logo.png';
                  }}
                />
                <p className="font-semibold text-blue-900">{profile.name}</p>
              </div>
              <div className="flex gap-4">
                <div className="bg-blue-100 px-4 py-2 rounded-lg">
                  <p className="text-xs text-blue-700">Streak</p>
                  <p className="font-bold text-blue-900">🔥 {profile.streak}</p>
                </div>
                <div className="bg-green-100 px-4 py-2 rounded-lg">
                  <p className="text-xs text-green-700">Points</p>
                  <p className="font-bold text-green-900">⭐ {profile.totalPoints}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Progress Overview */}
        <Card className="mb-8 border-2 border-blue-200 bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="text-2xl text-blue-900 flex items-center">
              <Target className="mr-2" /> Your Progress - {session.title}
            </CardTitle>
            <p className="text-sm text-gray-600 mt-2">{session.description}</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium text-blue-900">Days Completed</span>
                  <span className="text-sm font-bold text-blue-900">{completedDays} / {totalDays}</span>
                </div>
                <Progress value={(completedDays / totalDays) * 100} className="h-3" />
              </div>
              <div className="grid grid-cols-3 gap-4 mt-6">
                <div className="bg-blue-50 p-4 rounded-lg text-center border border-blue-200">
                  <Calendar className="mx-auto mb-2 text-blue-600" size={24} />
                  <p className="text-2xl font-bold text-blue-900">{currentDay}</p>
                  <p className="text-sm text-blue-700">Current Day</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg text-center border border-green-200">
                  <Trophy className="mx-auto mb-2 text-green-600" size={24} />
                  <p className="text-2xl font-bold text-green-900">{completedDays}</p>
                  <p className="text-sm text-green-700">Completed</p>
                </div>
                <div className="bg-amber-50 p-4 rounded-lg text-center border border-amber-200">
                  <BookOpen className="mx-auto mb-2 text-amber-600" size={24} />
                  <p className="text-2xl font-bold text-amber-900">{totalDays - completedDays}</p>
                  <p className="text-sm text-amber-700">Remaining</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Today's Lesson */}
        <Card className="mb-8 border-2 border-green-300 bg-gradient-to-br from-green-50 to-white shadow-lg">
          <CardHeader>
            <CardTitle className="text-2xl text-green-900">
              📖 Today's Lesson - {lessons.find(l => l.day_number === currentDay)?.reference || `Day ${currentDay}`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {lessons.find(l => l.day_number === currentDay) ? (
              <div>
                <h3 className="text-xl font-semibold text-green-900 mb-3">
                  {lessons.find(l => l.day_number === currentDay).title}
                </h3>
                <p className="text-green-800 mb-4 line-clamp-2">
                  {lessons.find(l => l.day_number === currentDay).scripture.substring(0, 150)}...
                </p>
                <Button
                  onClick={() => setSelectedLesson(lessons.find(l => l.day_number === currentDay))}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-3"
                >
                  Start Today's Lesson
                </Button>
              </div>
            ) : (
              <p className="text-green-800">Great job! You've completed all available lessons.</p>
            )}
          </CardContent>
        </Card>

        {/* Practice Previous Lessons */}
        <Card className="border-2 border-blue-200 bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="text-2xl text-blue-900">📚 Practice Previous Lessons</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {lessons.map((lesson) => {
                const isCompleted = userProgress?.completed_days?.includes(lesson.day_number);
                const isCurrent = lesson.day_number === currentDay;
                const isLocked = lesson.day_number > currentDay;

                return (
                  <div
                    key={lesson.lesson_id}
                    className={`rounded-lg border-2 transition-all ${
                      isLocked
                        ? 'bg-gray-100 border-gray-300 opacity-60'
                        : isCompleted
                        ? 'bg-green-50 border-green-300 hover:shadow-md'
                        : isCurrent
                        ? 'bg-blue-50 border-blue-300 hover:shadow-md'
                        : 'bg-white border-blue-200 hover:shadow-md'
                    }`}
                  >
                    <div 
                      className="p-4 cursor-pointer"
                      onClick={() => !isLocked && setSelectedLesson(lesson)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex-1">
                          <p className="font-semibold text-blue-900">
                            {lesson.reference}: {lesson.title}
                          </p>
                          <p className="text-sm text-blue-700 mt-1">
                            {isCompleted ? '✅ Completed' : isCurrent ? '🔵 Current' : isLocked ? '🔒 Locked' : '📖 Available'}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          {!isLocked && (
                            <div className="bg-green-100 px-3 py-2 rounded-lg border border-green-300">
                              <p className="text-xs text-green-700 font-medium">Your Points</p>
                              <p className="text-lg font-bold text-green-900">⭐ {lessonPoints[lesson.lesson_id] || 0}</p>
                            </div>
                          )}
                          {isCompleted && (
                            <Trophy className="text-green-600" size={24} />
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Lesson Leaderboard */}
                    {!isLocked && (
                      <div className="px-4 pb-4">
                        <MiniLeaderboard 
                          type="lesson"
                          lessonId={lesson.lesson_id}
                          profileId={profile.id}
                          title="Lesson Leaders (Top 3)"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
